package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.zq.astrock_ai.api.dto.PhoneNumbers
import org.zq.astrock_ai.api.dto.StaffRoles
import org.zq.astrock_ai.network.MarketApiClient
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.data.AuthSession
import org.zq.astrock_ai.ui.research.gushi.isPhoneWidth
import org.zq.astrock_ai.util.currentTimeMillis

@Composable
fun SmsLoginScreen(
    onLoggedIn: (AuthSession) -> Unit,
) {
    val api = remember { MarketApiClient() }
    val scope = rememberCoroutineScope()
    var phone by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    var message by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf(false) }
    var sending by remember { mutableStateOf(false) }
    var loggingIn by remember { mutableStateOf(false) }
    var cooldown by remember { mutableIntStateOf(0) }

    LaunchedEffect(cooldown) {
        if (cooldown <= 0) return@LaunchedEffect
        delay(1_000)
        cooldown -= 1
    }

    val fieldColors = OutlinedTextFieldDefaults.colors(
        focusedTextColor = StockColors.TextPrimary,
        unfocusedTextColor = StockColors.TextPrimary,
        focusedBorderColor = StockColors.PrimaryBlue,
        unfocusedBorderColor = StockColors.Border,
        cursorColor = StockColors.PrimaryBlue,
        focusedLabelColor = StockColors.TextSecondary,
        unfocusedLabelColor = StockColors.TextSecondary,
        focusedContainerColor = Color.White,
        unfocusedContainerColor = Color.White,
        disabledTextColor = StockColors.TextSecondary,
        disabledBorderColor = StockColors.Border,
        disabledLabelColor = StockColors.TextSecondary,
        disabledContainerColor = Color.White,
    )
    val phoneValid = PhoneNumbers.isCnMobile(phone)
    val testPhone = PhoneNumbers.normalize(phone) in setOf("15093137202", "18320985107")
    val codeValid = code.trim().length in 4..8
    val busy = sending || loggingIn

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .background(StockColors.Background),
        contentAlignment = Alignment.Center,
    ) {
        val phoneLayout = isPhoneWidth(maxWidth)
        Column(
            modifier = Modifier
                .widthIn(max = if (phoneLayout) 420.dp else 440.dp)
                .fillMaxWidth()
                .padding(horizontal = if (phoneLayout) 20.dp else 24.dp)
                .verticalScroll(rememberScrollState())
                .background(Color.White, RoundedCornerShape(16.dp))
                .padding(horizontal = 22.dp, vertical = 28.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = buildAnnotatedString {
                    withStyle(SpanStyle(color = StockColors.UpRed, fontWeight = FontWeight.Bold)) {
                        append("智启")
                    }
                    withStyle(SpanStyle(color = StockColors.TextPrimary, fontWeight = FontWeight.Bold)) {
                        append("股势")
                    }
                },
                fontSize = 28.sp,
            )
            Spacer(Modifier.height(8.dp))
            Text(
                "手机短信登录",
                color = StockColors.TextPrimary,
                fontWeight = FontWeight.SemiBold,
                fontSize = 18.sp,
            )
            Spacer(Modifier.height(6.dp))
            Text(
                "登录成功后本机记住账号，下次自动进入",
                color = StockColors.TextSecondary,
                fontSize = 12.sp,
            )
            Spacer(Modifier.height(22.dp))
            OutlinedTextField(
                value = phone,
                onValueChange = { value ->
                    phone = value.filter { it.isDigit() }.take(11)
                    message = null
                },
                label = { Text("手机号") },
                placeholder = { Text("请输入11位手机号") },
                singleLine = true,
                enabled = !busy,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                colors = fieldColors,
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(Modifier.height(12.dp))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                OutlinedTextField(
                    value = code,
                    onValueChange = { value ->
                        code = value.filter { it.isDigit() }.take(6)
                        message = null
                    },
                    label = { Text("验证码") },
                    placeholder = { Text("6位数字") },
                    singleLine = true,
                    enabled = !busy,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    colors = fieldColors,
                    modifier = Modifier.weight(1f),
                )
                OutlinedButton(
                    onClick = {
                        if (!phoneValid) {
                            error = true
                            message = "请输入11位中国大陆手机号"
                            return@OutlinedButton
                        }
                        sending = true
                        error = false
                        message = null
                        scope.launch {
                            val result = runCatching { api.sendSmsCode(phone) }.getOrElse { err ->
                                val detail = err.message.orEmpty()
                                org.zq.astrock_ai.api.dto.SmsSendResponseDto(
                                    success = false,
                                    message = when {
                                        detail.contains("404") ->
                                            "验证码接口不存在，请确认服务端已部署短信登录"
                                        detail.contains("Failed to fetch", ignoreCase = true) ||
                                            detail.contains("Connection", ignoreCase = true) ->
                                            "无法连接服务器，请检查网络或后端地址"
                                        else -> "网络异常，请稍后重试"
                                    },
                                )
                            }
                            sending = false
                            error = !result.success
                            message = result.message
                            if (result.success) {
                                cooldown = result.resendWaitSeconds.toInt().coerceAtLeast(60)
                            } else if (result.resendWaitSeconds > 0) {
                                cooldown = result.resendWaitSeconds.toInt()
                            }
                        }
                    },
                    enabled = !busy && cooldown <= 0,
                    modifier = Modifier
                        .heightIn(min = 48.dp)
                        .padding(top = 4.dp),
                    colors = ButtonDefaults.outlinedButtonColors(
                        contentColor = StockColors.PrimaryBlue,
                    ),
                ) {
                    Text(
                        when {
                            sending -> "发送中"
                            cooldown > 0 -> "${cooldown}s"
                            else -> "获取验证码"
                        },
                        fontSize = 13.sp,
                        maxLines = 1,
                    )
                }
            }
            if (testPhone) {
                Spacer(Modifier.height(8.dp))
                Text(
                    "",
                    color = StockColors.TextSecondary,
                    fontSize = 12.sp,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
            if (!message.isNullOrBlank()) {
                Spacer(Modifier.height(10.dp))
                Text(
                    message.orEmpty(),
                    color = if (error) StockColors.UpRed else StockColors.DownGreen,
                    fontSize = 12.sp,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
            Spacer(Modifier.height(18.dp))
            Button(
                onClick = {
                    if (!phoneValid) {
                        error = true
                        message = "请输入11位中国大陆手机号"
                        return@Button
                    }
                    if (!codeValid) {
                        error = true
                        message = "请输入短信验证码"
                        return@Button
                    }
                    loggingIn = true
                    error = false
                    message = null
                    scope.launch {
                        val result = runCatching { api.loginWithSms(phone, code) }.getOrElse {
                            org.zq.astrock_ai.api.dto.SmsLoginResponseDto(
                                success = false,
                                message = "网络异常，请稍后重试",
                            )
                        }
                        loggingIn = false
                        if (result.success && result.token.isNotBlank()) {
                            onLoggedIn(
                                AuthSession(
                                    phone = result.phone.ifBlank { PhoneNumbers.normalize(phone) },
                                    token = result.token,
                                    loggedAtMs = currentTimeMillis(),
                                    superAdmin = StaffRoles.isSuper(result.role, result.superAdmin),
                                    role = StaffRoles.normalize(result.role, result.superAdmin),
                                ),
                            )
                        } else {
                            error = true
                            message = result.message.ifBlank { "登录失败" }
                        }
                    }
                },
                enabled = !busy,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(48.dp),
                colors = ButtonDefaults.buttonColors(containerColor = StockColors.PrimaryBlue),
                shape = RoundedCornerShape(10.dp),
            ) {
                Text(if (loggingIn) "登录中…" else "登录", fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
            }
            Spacer(Modifier.height(12.dp))
            Text(
                "未注册的手机号验证后将自动开通",
                color = StockColors.Neutral,
                fontSize = 11.sp,
            )
        }
    }
}
